import { Box } from "@repo/components/src/ui/box";
import { Image } from "@repo/components/src/ui/image";
import React from "react";

type Animation = "slow" | "medium" | "fast" | "none";

type Props = {
  source: number;
  /** Literal Tailwind size classes (e.g. `"h-[230px] w-[230px]"`) so the JIT
   * can see them at the call site — the shared `Image` drops the `style` prop,
   * so sizing must travel as classes. */
  sizeClassName: string;
  /** Caller owns absolute positioning + rotation (arbitrary classes per illustration). */
  className?: string;
  animation?: Animation;
};

const ANIMATION_CLASS: Record<Animation, string> = {
  slow: "motion-safe:animate-floaty-9",
  medium: "motion-safe:animate-floaty-8",
  fast: "motion-safe:animate-floaty-7",
  none: "",
};

/**
 * One of the 3D glass illustrations drifting behind `ComposerPanel`'s copy.
 * The float animation lives on this wrapper `Box` — separate from any rotation
 * the caller applies on its own wrapper — because the `floaty` keyframe sets
 * `transform` wholesale. `prefers-reduced-motion` disables the drift
 * (`motion-safe:`) while leaving the static rotation untouched.
 */
const FloatingIllustration = React.memo(
  ({ source, sizeClassName, className, animation = "none" }: Props) => (
    <Box
      pointerEvents="none"
      className={`${ANIMATION_CLASS[animation]} ${sizeClassName} ${className ?? ""}`}
    >
      <Image source={source} alt="" size="full" contentFit="contain" />
    </Box>
  ),
);

FloatingIllustration.displayName = "FloatingIllustration";

export default FloatingIllustration;
